import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, message, Spin, Result } from 'antd';
import {
  DownloadOutlined,
  LockOutlined,
  FileOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { cloudDiskAPI } from '../services/api';
import './CloudDiskShare.css';

interface ShareInfo {
  share_code: string;
  original_name: string;
  file_size: number;
  file_type: string;
  has_password: boolean;
  max_downloads: number;
  download_count: number;
  expires_at: string | null;
  created_at: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const CloudDiskShare: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [error, setError] = useState('');
  const [extractionCode, setExtractionCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!code) {
      setError('分享码不能为空');
      setLoading(false);
      return;
    }
    fetchShareInfo();
  }, [code]);

  const fetchShareInfo = async () => {
    setLoading(true);
    try {
      const res = await cloudDiskAPI.getShareInfo(code!);
      if (res.data?.status === 200) {
        setShareInfo(res.data.data);
        if (!res.data.data.has_password) {
          setVerified(true);
        }
      } else {
        setError(res.data?.error || '分享不存在或已失效');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || '获取分享信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!extractionCode.trim()) {
      message.warning('请输入提取码');
      return;
    }
    setVerifying(true);
    try {
      const res = await cloudDiskAPI.verifyCode(code!, extractionCode.trim());
      if (res.data?.status === 200) {
        setVerified(true);
        message.success('验证成功');
      } else {
        message.error(res.data?.error || '提取码错误');
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || '验证失败');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownload = async () => {
    if (!shareInfo) return;
    setDownloading(true);
    try {
      const url = cloudDiskAPI.getDownloadUrl(code!, shareInfo.has_password ? extractionCode.trim() : undefined);
      const a = document.createElement('a');
      a.href = url;
      a.download = shareInfo.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      message.success('开始下载，请稍候');
      setTimeout(() => {
        fetchShareInfo();
      }, 2000);
    } catch (e: any) {
      message.error('下载失败');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="cloud-disk-share-page">
        <div className="cloud-disk-share-container">
          <div className="cloud-disk-share-loading">
            <Spin size="large" />
            <p>正在获取分享信息...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cloud-disk-share-page">
        <div className="cloud-disk-share-container">
          <Result
            status="error"
            icon={<ExclamationCircleOutlined />}
            title="分享不可用"
            subTitle={error}
            extra={[
              <Button type="primary" key="back" onClick={() => navigate('/')}>
                返回首页
              </Button>,
            ]}
          />
        </div>
      </div>
    );
  }

  if (!shareInfo) {
    return null;
  }

  return (
    <div className="cloud-disk-share-page">
      <div className="cloud-disk-share-container">
        <div className="cloud-disk-share-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            className="cloud-disk-share-back"
          >
            返回首页
          </Button>
          <h1 className="cloud-disk-share-title">
            <FileOutlined /> 文件分享下载
          </h1>
        </div>

        <div className="cloud-disk-share-body">
          <div className="cloud-disk-share-file-card">
            <div className="cloud-disk-share-file-icon">
              <FileOutlined />
            </div>
            <div className="cloud-disk-share-file-info">
              <div className="cloud-disk-share-file-name" title={shareInfo.original_name}>
                {shareInfo.original_name}
              </div>
              <div className="cloud-disk-share-file-meta">
                <span>大小: {formatFileSize(shareInfo.file_size)}</span>
                {shareInfo.file_type && <span>类型: {shareInfo.file_type}</span>}
                <span>上传时间: {new Date(shareInfo.created_at).toLocaleString()}</span>
              </div>
              {shareInfo.max_downloads > 0 && (
                <div className="cloud-disk-share-download-info">
                  下载次数: {shareInfo.download_count} / {shareInfo.max_downloads}
                </div>
              )}
              {shareInfo.expires_at && (
                <div className="cloud-disk-share-expire-info">
                  过期时间: {new Date(shareInfo.expires_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>

          {shareInfo.has_password && !verified && (
            <div className="cloud-disk-share-verify">
              <h3><LockOutlined /> 此文件需要提取码</h3>
              <p>请输入分享者提供的提取码以继续下载</p>
              <div className="cloud-disk-share-verify-input">
                <Input.Password
                  placeholder="请输入提取码"
                  value={extractionCode}
                  onChange={(e) => setExtractionCode(e.target.value)}
                  prefix={<LockOutlined />}
                  onPressEnter={handleVerify}
                  size="large"
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleVerify}
                  loading={verifying}
                >
                  验证
                </Button>
              </div>
            </div>
          )}

          {verified && (
            <div className="cloud-disk-share-download">
              {shareInfo.has_password && (
                <div className="cloud-disk-share-verified">
                  <CheckCircleOutlined className="cloud-disk-share-verified-icon" />
                  <span>验证通过，可以下载文件</span>
                </div>
              )}
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                loading={downloading}
                className="cloud-disk-share-download-btn"
              >
                下载文件
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudDiskShare;
